import { useSearchParams, useRouter } from "next/navigation";
import { PaymentSuccessfulComponent } from "../payment-successful";
import { format } from "date-fns";
import { useEffect, useState, useRef } from "react";
import { useQueryState } from "nuqs";
import { parseAsIsoDateTime, parseAsInteger, parseAsString } from "nuqs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clearCompletedBookingIntent } from "~/server/actions/booking-cart-actions";
import { useSession } from "next-auth/react";
import { capturePayPalOrderAction } from "~/server/actions/paypal-actions";
import { TherapyRelationshipType } from "~/prisma/browser";
import { AlertCircle, Loader2 } from "lucide-react";
import { createMetaEventId, META_EVENT_NAME_MAP } from "~/lib/meta-tracking";
import { getClientTrackingDebugCode } from "~/lib/client/test-diagnostics";
import { cleanPaymentParamsFromUrl } from "~/lib/payment/url";
import {
  resolveGa4PurchaseEventName,
  trackReliableGA4PurchaseEvent,
} from "~/lib/ga4-purchase";
import { api } from "~/trpc/react";
import { Button } from "~/components/ui/button";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { hasBrowserAnalyticsConsent } from "~/lib/consent-region";
import { consumeSpecialtyDirectoryBookingAttribution } from "~/lib/client/specialty-directory-booking-attribution";

const inFlightPayPalCaptureRequests = new Map<string, Promise<void>>();

interface StepPaymentReturnProps {
  therapistName: string;
  therapistId: string;
  isLogged?: boolean;
}

export default function StepPaymentReturn(props: StepPaymentReturnProps) {
  const searchParams = useSearchParams();
  const [sessionDate] = useQueryState("date", parseAsIsoDateTime);
  const [therapyType] = useQueryState("therapyType", parseAsString);
  const [paypalToken] = useQueryState("token", parseAsString);
  const [sessionQuantity] = useQueryState("sessionQuantity", parseAsInteger);
  const [proposalId] = useQueryState("proposalId", parseAsInteger);
  const router = useRouter();
  const queryClient = useQueryClient();
  const bookingIntentCleanupStarted = useRef(false);
  const [isClient, setIsClient] = useState(false);
  const [, setStep] = useQueryState("step", parseAsInteger.withDefault(3));
  const [isCapturingPayPal, setIsCapturingPayPal] = useState(false);
  const [hasCapturedPayPalOrder, setHasCapturedPayPalOrder] = useState(false);
  const paypalCaptureInitiated = useRef(false);
  const metaPurchaseTracked = useRef(false);
  const ga4PurchaseTracked = useRef(false);
  const specialtyDirectoryPurchaseTracked = useRef(false);
  const [initialPaymentParams, setInitialPaymentParams] = useState<{
    redirectStatus: string | null;
    stripePaymentIntentId: string | null;
    paypalOrderId: string | null;
    conversionValue: number | null;
    trackingEventName: string | null;
  } | null>(null);

  const redirectStatus = initialPaymentParams?.redirectStatus ?? null;
  const stripePaymentIntentId =
    initialPaymentParams?.stripePaymentIntentId ?? null;
  const paypalOrderId = initialPaymentParams?.paypalOrderId ?? null;
  const shouldVerifyStripeFulfillment =
    isClient &&
    redirectStatus === "succeeded" &&
    Boolean(stripePaymentIntentId);
  const [hasStripeFulfillmentTimedOut, setHasStripeFulfillmentTimedOut] =
    useState(false);

  const capturePayPalOrder = useMutation({
    mutationFn: capturePayPalOrderAction,
  });
  const { data: session, status: sessionStatus } = useSession();
  const stripeFulfillment =
    api.paymentIntent.getBookingFulfillmentStatus.useQuery(
      { paymentReferenceId: stripePaymentIntentId ?? "" },
      {
        enabled: shouldVerifyStripeFulfillment,
        refetchInterval: (query) =>
          query.state.data?.fulfilled ? false : 1000,
      },
    );
  const hasConfirmedStripeFulfillment =
    redirectStatus === "succeeded" &&
    Boolean(stripePaymentIntentId) &&
    Boolean(stripeFulfillment.data?.fulfilled);

  useEffect(() => {
    if (
      (!hasConfirmedStripeFulfillment && !hasCapturedPayPalOrder) ||
      !sessionDate ||
      bookingIntentCleanupStarted.current
    )
      return;
    bookingIntentCleanupStarted.current = true;
    void clearCompletedBookingIntent({
      therapistId: props.therapistId,
      selectedDateTime: sessionDate.toISOString(),
      therapyType:
        (therapyType as TherapyRelationshipType | null) ??
        TherapyRelationshipType.INDIVIDUAL,
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ["booking-cart"] }))
      .catch((error: unknown) => {
        bookingIntentCleanupStarted.current = false;
        console.error("Failed to clear completed booking intent:", error);
      });
  }, [
    hasConfirmedStripeFulfillment,
    hasCapturedPayPalOrder,
    sessionDate,
    props.therapistId,
    therapyType,
    queryClient,
  ]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || initialPaymentParams) {
      return;
    }

    const currentSearchParams = new URLSearchParams(window.location.search);
    const cvParam = currentSearchParams.get("cv");
    setInitialPaymentParams({
      redirectStatus: currentSearchParams.get("redirect_status"),
      stripePaymentIntentId: currentSearchParams.get("payment_intent"),
      paypalOrderId: currentSearchParams.get("token") ?? paypalToken,
      conversionValue: cvParam ? Number(cvParam) : null,
      trackingEventName: currentSearchParams.get("te"),
    });
  }, [initialPaymentParams, isClient, paypalToken]);

  useEffect(() => {
    if (!isClient || redirectStatus !== "failed") return;
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("step", "2");
    currentUrl.searchParams.set("paymentFailed", "1");
    router.push(currentUrl.pathname + currentUrl.search);
  }, [isClient, redirectStatus, router]);

  useEffect(() => {
    if (
      !isClient ||
      !paypalOrderId ||
      paypalCaptureInitiated.current ||
      redirectStatus
    )
      return;

    const captureOrder = async () => {
      const captureGuardKey = `paypal_capture_started:${paypalOrderId}`;
      const existingState = window.sessionStorage.getItem(captureGuardKey);
      if (existingState === "done") {
        paypalCaptureInitiated.current = true;
        setHasCapturedPayPalOrder(true);
        return;
      }

      let capturePromise = inFlightPayPalCaptureRequests.get(paypalOrderId);

      if (!capturePromise) {
        window.sessionStorage.setItem(captureGuardKey, "started");
        capturePromise = capturePayPalOrder
          .mutateAsync({
            orderId: paypalOrderId,
            therapistId: props.therapistId,
            therapyType:
              (therapyType as TherapyRelationshipType) ??
              TherapyRelationshipType.INDIVIDUAL,
            sessionTimestamp: sessionDate?.toISOString(),
            sessionQuantity: sessionQuantity ?? 1,
            ...(proposalId != null ? { proposalMessageId: proposalId } : {}),
            debugTestCode: getClientTrackingDebugCode(),
          })
          .then(() => {
            window.sessionStorage.setItem(captureGuardKey, "done");
          })
          .catch((err: unknown) => {
            window.sessionStorage.removeItem(captureGuardKey);
            throw err;
          })
          .finally(() => {
            inFlightPayPalCaptureRequests.delete(paypalOrderId);
          });
        inFlightPayPalCaptureRequests.set(paypalOrderId, capturePromise);
      }

      paypalCaptureInitiated.current = true;
      setIsCapturingPayPal(true);
      try {
        await capturePromise;
        setHasCapturedPayPalOrder(true);
      } catch (err) {
        console.error("PayPal capture error:", err);

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("step", "2");
        currentUrl.searchParams.set("redirect_status", "failed");
        currentUrl.searchParams.set("paymentFailed", "1");
        currentUrl.searchParams.set("paymentCaptureUncertain", "1");
        currentUrl.searchParams.delete("token");
        currentUrl.searchParams.delete("PayerID");
        currentUrl.searchParams.delete("sessionQuantity");
        router.push(currentUrl.pathname + currentUrl.search);
      } finally {
        setIsCapturingPayPal(false);
      }
    };

    void captureOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClient,
    paypalOrderId,
    redirectStatus,
    // Excluding capturePayPalOrder - TanStack Query mutations are stable
    props.therapistId,
    therapyType,
    sessionDate,
    sessionQuantity,
    proposalId,
    router,
  ]);

  useEffect(() => {
    if (specialtyDirectoryPurchaseTracked.current) return;

    const paymentId = hasConfirmedStripeFulfillment
      ? stripePaymentIntentId
      : hasCapturedPayPalOrder
        ? paypalOrderId
        : null;

    if (!paymentId) return;

    const attribution = consumeSpecialtyDirectoryBookingAttribution({
      therapistId: props.therapistId,
    });
    if (!attribution || !hasBrowserAnalyticsConsent()) return;

    capturePostHogEvent("specialty_directory_booking_completed");
    specialtyDirectoryPurchaseTracked.current = true;
  }, [
    hasCapturedPayPalOrder,
    hasConfirmedStripeFulfillment,
    paypalOrderId,
    props.therapistId,
    stripePaymentIntentId,
  ]);

  useEffect(() => {
    if (!sessionDate) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("step", "0");
      router.push(currentUrl.pathname + currentUrl.search);
      void setStep(0);
    }
  }, [sessionDate, router, setStep]);

  useEffect(() => {
    if (!isClient) return;
    if (hasConfirmedStripeFulfillment || hasCapturedPayPalOrder) {
      cleanPaymentParamsFromUrl(new URL(window.location.href));
    }
  }, [hasCapturedPayPalOrder, hasConfirmedStripeFulfillment, isClient]);

  useEffect(() => {
    if (!shouldVerifyStripeFulfillment || stripeFulfillment.data?.fulfilled) {
      setHasStripeFulfillmentTimedOut(false);
      return;
    }

    setHasStripeFulfillmentTimedOut(false);
    const timer = window.setTimeout(
      () => setHasStripeFulfillmentTimedOut(true),
      15000,
    );

    return () => window.clearTimeout(timer);
  }, [
    shouldVerifyStripeFulfillment,
    stripePaymentIntentId,
    stripeFulfillment.data?.fulfilled,
  ]);

  useEffect(() => {
    if (ga4PurchaseTracked.current) return;
    if (props.isLogged && sessionStatus === "loading") return;

    const paymentId = hasConfirmedStripeFulfillment
      ? stripePaymentIntentId
      : hasCapturedPayPalOrder
        ? paypalOrderId
        : null;

    if (!paymentId) return;

    const value = initialPaymentParams?.conversionValue;
    if (value == null || value <= 0) return;

    const trackingEventName = resolveGa4PurchaseEventName(
      initialPaymentParams?.trackingEventName,
    );
    if (!trackingEventName) return;

    const fired = trackReliableGA4PurchaseEvent(
      trackingEventName,
      {
        transaction_id: paymentId,
        value,
        currency: "EUR",
        items: [
          {
            item_id: props.therapistId,
            item_name: props.therapistName,
            price: value,
            quantity: 1,
          },
        ],
      },
      props.isLogged && session?.user.email
        ? { email: session.user.email }
        : undefined,
    );

    if (fired) {
      ga4PurchaseTracked.current = true;
    }
  }, [
    hasCapturedPayPalOrder,
    hasConfirmedStripeFulfillment,
    paypalOrderId,
    stripePaymentIntentId,
    initialPaymentParams,
    props.isLogged,
    props.therapistId,
    props.therapistName,
    session?.user.email,
    sessionStatus,
  ]);

  useEffect(() => {
    if (metaPurchaseTracked.current) return;

    const paymentId = hasConfirmedStripeFulfillment
      ? stripePaymentIntentId
      : hasCapturedPayPalOrder
        ? paypalOrderId
        : null;

    if (!paymentId) return;

    const value = initialPaymentParams?.conversionValue;
    const trackingEventName = initialPaymentParams?.trackingEventName;
    if (
      value == null ||
      value <= 0 ||
      (trackingEventName !== "purchase" &&
        trackingEventName !== "repeat_purchase")
    ) {
      return;
    }

    const metaEventName = META_EVENT_NAME_MAP[trackingEventName];
    if (!metaEventName) return;

    function fireMetaPurchase(): boolean {
      if (typeof window === "undefined" || !window.fbq) return false;
      const therapyCategory =
        searchParams?.get("therapyType") === "COUPLES" ? "duo" : "solo";
      window.fbq(
        "trackCustom",
        metaEventName,
        {
          currency: "EUR",
          value,
          content_type: "service",
          content_ids: [props.therapistId],
          content_category: therapyCategory,
        },
        { eventID: createMetaEventId(trackingEventName!, paymentId!) },
      );
      return true;
    }

    if (fireMetaPurchase()) {
      metaPurchaseTracked.current = true;
      return;
    }

    // Retry when user grants consent (fbq becomes available)
    function onConsentChange(event: StorageEvent) {
      if (event.key !== "cookieConsent" || metaPurchaseTracked.current) return;
      if (fireMetaPurchase()) {
        metaPurchaseTracked.current = true;
      }
    }

    window.addEventListener("storage", onConsentChange);
    return () => window.removeEventListener("storage", onConsentChange);
  }, [
    hasCapturedPayPalOrder,
    hasConfirmedStripeFulfillment,
    paypalOrderId,
    stripePaymentIntentId,
    initialPaymentParams,
    props.therapistId,
    searchParams,
  ]);

  if (isCapturingPayPal) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Finalizacija plaćanja...</h3>
          <p className="text-sm text-muted-foreground">
            Sačekaj trenutak dok finalizujemo tvoje PayPal plaćanje
          </p>
        </div>
      </div>
    );
  }

  if (redirectStatus === "succeeded" && !stripePaymentIntentId) {
    return (
      <StripeFulfillmentProblem
        title="Ne možemo da proverimo uplatu"
        message="Stripe nas je vratio bez ID-ja uplate. Osveži stranicu ili se javi podršci."
      />
    );
  }

  if (
    shouldVerifyStripeFulfillment &&
    !stripeFulfillment.data?.fulfilled &&
    !hasStripeFulfillmentTimedOut &&
    !stripeFulfillment.isError
  ) {
    return (
      <PaymentFinalizationState
        title="Finalizacija plaćanja..."
        message="Plaćanje je prošlo kroz Stripe. Čekamo potvrdu servera da zakažemo termin."
      />
    );
  }

  if (
    shouldVerifyStripeFulfillment &&
    !stripeFulfillment.data?.fulfilled &&
    (hasStripeFulfillmentTimedOut || stripeFulfillment.isError)
  ) {
    return (
      <StripeFulfillmentProblem
        title="Plaćanje je primljeno, ali sesija još nije zakazana"
        message="Ne pokušavaj ponovo odmah. Stripe potvrda nije stigla do aplikacije. Proveri ponovo za nekoliko sekundi."
        paymentIntentId={stripePaymentIntentId}
      />
    );
  }

  const formattedDate =
    isClient && sessionDate ? format(sessionDate, "dd/MM/yyyy") : "";
  const formattedTime =
    isClient && sessionDate ? format(sessionDate, "HH:mm") : "";

  return (
    <PaymentSuccessfulComponent
      sessionDate={formattedDate}
      sessionTime={formattedTime}
      therapistName={props.therapistName}
      therapistId={props.therapistId}
      therapyType={
        (therapyType as TherapyRelationshipType | null) ??
        TherapyRelationshipType.INDIVIDUAL
      }
      relationshipId={stripeFulfillment.data?.therapyRelationshipId}
      isLogged={props.isLogged}
    />
  );
}

function PaymentFinalizationState(props: { title: string; message: string }) {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 px-6">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <div className="max-w-md text-center">
        <h3 className="text-lg font-semibold">{props.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{props.message}</p>
      </div>
    </div>
  );
}

function StripeFulfillmentProblem(props: {
  title: string;
  message: string;
  paymentIntentId?: string | null;
}) {
  return (
    <div className="flex min-h-[400px] items-center justify-center px-6">
      <div className="w-full max-w-md rounded-lg border border-destructive/30 bg-destructive/5 p-5">
        <div className="flex gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <h3 className="font-semibold text-destructive">{props.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {props.message}
            </p>
            {props.paymentIntentId && (
              <p className="mt-2 break-all text-xs text-muted-foreground">
                ID uplate: {props.paymentIntentId}
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Proveri ponovo
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
